// Copyright 2023 Carnegie Mellon University. All Rights Reserved.
// Released under a MIT (SEI)-style license. See LICENSE.md in the
// project root for license information.

import { MselStore } from './msel.store';
import { MselQuery } from './msel.query';
import { Injectable } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { Router, ActivatedRoute } from '@angular/router';
import { HttpEventType, HttpResponse } from '@angular/common/http';
import {
  Card,
  DataField,
  DataFieldType,
  IntegrationType,
  MselItemStatus,
  Move,
  Msel,
  MselImportResult,
  MselRole,
  MselService,
  Unit,
  ScenarioEvent,
  Team,
  UserMselRole,
} from 'src/app/generated/blueprint.api';
import { map, shareReplay, take, tap } from 'rxjs/operators';
import { BehaviorSubject, Observable, Subject, combineLatest } from 'rxjs';
import { ErrorService } from 'src/app/services/error/error.service';

@Injectable({
  providedIn: 'root',
})
export class MselPlus implements Msel {
  dateCreated?: Date;
  dateModified?: Date;
  createdBy?: string;
  modifiedBy?: string;
  id?: string;
  name: string;
  description?: string;
  status?: MselItemStatus;
  integrationStatus?: string;
  usePlayer?: boolean;
  playerViewId?: string;
  useGallery?: boolean;
  galleryCollectionId?: string;
  galleryExhibitId?: string;
  useCite?: boolean;
  citeEvaluationId?: string;
  citeScoringModelId?: string;
  useSteamfitter?: boolean;
  steamfitterScenarioId?: string;
  useCaster?: boolean;
  casterDirectoryId?: string;
  casterWorkspaceId?: string;
  casterRunId?: string;
  isTemplate?: boolean;
  startTime?: Date;
  durationSeconds?: number;
  showTimeOnScenarioEventList?: boolean;
  showTimeOnExerciseView?: boolean;
  timeDisplayOrder?: number;
  showMoveOnScenarioEventList?: boolean;
  showMoveOnExerciseView?: boolean;
  moveDisplayOrder?: number;
  showGroupOnScenarioEventList?: boolean;
  showGroupOnExerciseView?: boolean;
  groupDisplayOrder?: number;
  showIntegrationTargetOnScenarioEventList?: boolean;
  showIntegrationTargetOnExerciseView?: boolean;
  integrationTargetDisplayOrder?: number;
  showTimeOnAssessorView?: boolean;
  showMoveOnAssessorView?: boolean;
  showGroupOnAssessorView?: boolean;
  showIntegrationTargetOnAssessorView?: boolean;
  moves?: Array<Move>;
  dataFields?: Array<DataField>;
  scenarioEvents?: Array<ScenarioEvent>;
  teams?: Array<Team>;
  units?: Array<Unit>;
  userMselRoles?: Array<UserMselRole>;
  headerRowMetadata?: string;
  cards?: Array<Card>;
  galleryArticleParameters?: Array<string>;
  gallerySourceTypes?: Array<string>;
  galleryItemStatuses?: Array<string>;
  playerIntegrationType?: IntegrationType;
  galleryIntegrationType?: IntegrationType;
  citeIntegrationType?: IntegrationType;
  steamfitterIntegrationType?: IntegrationType;
  educationalLevel?: string;
  subject?: string;
  keywords?: string;
  educationalUse?: string;
  courseMode?: string;
  language?: string;

  hasRole(userId: string, scenarioEventId: string) {
    // initialize to no roles
    let mselRole = {
      owner: false,
      moveEditor: false,
      approver: false,
      editor: false,
      evaluator: false,
      viewer: false,
    };
    // set owner role
    mselRole.owner =
      this.createdBy === userId ||
      (this.userMselRoles &&
        this.userMselRoles.some(
          (umr) => umr.userId === userId && umr.role === MselRole.Owner
        ));
    mselRole.moveEditor = !this.userMselRoles
      ? false
      : this.userMselRoles.some(
        (umr) => umr.userId === userId && umr.role === MselRole.MoveEditor
      );
    // owners can do everything
    if (mselRole.owner) {
      mselRole.approver = true;
      mselRole.editor = true;
      mselRole.moveEditor = true;
      mselRole.evaluator = true;
      mselRole.viewer = true;
      // set roles for a particular scenario event for a non-owner
    } else if (
      this.scenarioEvents &&
      this.scenarioEvents.length > 0 &&
      scenarioEventId
    ) {
      const scenarioEvent = this.scenarioEvents.find(
        (se) => se.id === scenarioEventId
      );
      const assignedToDataField = this.dataFields.find(
        (df) => df.dataType === DataFieldType.Team
      );
      const dataValue =
        assignedToDataField && scenarioEvent
          ? scenarioEvent.dataValues.find(
            (dv) => dv.dataFieldId === assignedToDataField.id
          )
          : null;
      if (dataValue) {
        const unit = this.units.find((t) => t.shortName === dataValue.value);
        const isInUnit =
          unit && unit.users && unit.users.some((u) => u.id === userId);
        if (isInUnit) {
          mselRole = this.setMselRoles(userId, mselRole);
        }
      }
      // set roles for the MSEL for a non-owner, if no scenario event is supplied
    } else {
      mselRole = this.setMselRoles(userId, mselRole);
    }

    return mselRole;
  }

  private setMselRoles(userId: string, mselRole: any) {
    if (this.userMselRoles && this.userMselRoles.length > 0) {
      mselRole.approver = this.userMselRoles.some(
        (umr) => umr.userId === userId && umr.role === MselRole.Approver
      );
      mselRole.editor =
        mselRole.approver ||
        this.userMselRoles.some(
          (umr) => umr.userId === userId && umr.role === MselRole.Editor
        );
      mselRole.evaluator = this.userMselRoles.some(
        (umr) => umr.userId === userId && umr.role === MselRole.Evaluator
      );
      mselRole.viewer =
        mselRole.approver ||
        mselRole.editor ||
        mselRole.moveEditor ||
        mselRole.evaluator ||
        this.userMselRoles.some(
          (umr) => umr.userId === userId && umr.role === MselRole.Viewer
        );
    }
    return mselRole;
  }
}

@Injectable({
  providedIn: 'root',
})
export class MselDataService {
  readonly MselList: Observable<Msel[]>;
  readonly filterControl = new UntypedFormControl();
  private _requestedMselId: string;
  private _requestedMselId$ = this.activatedRoute.queryParamMap.pipe(
    map((params) => params.get('mselId') || '')
  );
  private filterTerm: Observable<string>;
  private sortColumn: Observable<string>;
  private sortIsAscending: Observable<boolean>;
  private _pageEvent: PageEvent = { length: 0, pageIndex: 0, pageSize: 10 };
  readonly pageEvent = new BehaviorSubject<PageEvent>(this._pageEvent);
  private pageSize: Observable<number>;
  private pageIndex: Observable<number>;
  public uploadProgress = new Subject<number>();

  constructor(
    private mselStore: MselStore,
    private mselQuery: MselQuery,
    private mselService: MselService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private errorService: ErrorService
  ) {
    this.filterTerm = activatedRoute.queryParamMap.pipe(
      map((params) => params.get('mselmask') || '')
    );
    this.filterControl.valueChanges.subscribe((term) => {
      this.router.navigate([], {
        queryParams: { mselmask: term },
        queryParamsHandling: 'merge',
      });
    });
    this.sortColumn = activatedRoute.queryParamMap.pipe(
      map((params) => params.get('sorton') || 'name')
    );
    this.sortIsAscending = activatedRoute.queryParamMap.pipe(
      map((params) => (params.get('sortdir') || 'asc') === 'asc')
    );
    this.pageSize = activatedRoute.queryParamMap.pipe(
      map((params) => parseInt(params.get('pagesize') || '20', 10))
    );
    this.pageIndex = activatedRoute.queryParamMap.pipe(
      map((params) => parseInt(params.get('pageindex') || '0', 10))
    );
    this.MselList = combineLatest([
      this.mselQuery.selectAll(),
      this.filterTerm,
      this.sortColumn,
      this.sortIsAscending,
      this.pageSize,
      this.pageIndex,
    ]).pipe(
      map(
        ([
          items,
          filterTerm,
          sortColumn,
          sortIsAscending,
          pageSize,
          pageIndex,
        ]) =>
          items
            ? (items as Msel[])
              .sort((a: Msel, b: Msel) =>
                this.sortMsels(a, b, sortColumn, sortIsAscending)
              )
              .filter(
                (msel) =>
                  ('' + msel.description)
                    .toLowerCase()
                    .includes(filterTerm.toLowerCase()) ||
                  msel.id.toLowerCase().includes(filterTerm.toLowerCase())
              )
            : []
      )
    );
  }

  load() {
    this.mselStore.setLoading(true);
    this.mselService
      .getMsels()
      .pipe(
        tap(() => {
          this.mselStore.setLoading(false);
        }),
        take(1)
      )
      .subscribe(
        (msels) => {
          msels.forEach(a => {
            this.setAsDates(a);
          });
          this.mselStore.set(msels);
        },
        (error) => {
          this.mselStore.set([]);
          this.mselStore.setLoading(false);
        }
      );
  }

  loadMine() {
    this.mselStore.setLoading(true);
    this.mselService
      .getMyMsels()
      .pipe(
        tap(() => {
          this.mselStore.setLoading(false);
        }),
        take(1)
      )
      .subscribe(
        (msels) => {
          msels.forEach(a => {
            this.setAsDates(a);
          });
          this.mselStore.set(msels);
        },
        (error) => {
          this.mselStore.set([]);
          this.mselStore.setLoading(false);
        }
      );
  }

  getMyJoinMsels() {
    return this.mselService.getMyJoinMsels();
  }

  getMyLaunchMsels() {
    return this.mselService.getMyLaunchMsels();
  }

  getMyBuildMsels() {
    return this.mselService.getMyMsels();
  }

  loadById(id: string) {
    this.mselStore.setLoading(true);
    return this.mselService
      .getMsel(id)
      .pipe(
        tap(() => {
          this.mselStore.setLoading(false);
        }),
        take(1)
      )
      .subscribe(
        (s) => {
          this.setAsDates(s);
          this.mselStore.upsert(s.id, { ...s });
        },
        (error) => {
          this.mselStore.setLoading(false);
        }
      );
  }

  unload() {
    this.mselStore.set([]);
  }

  add(msel: Msel): Observable<Msel> {
    this.mselStore.setLoading(true);
    return this.mselService
      .createMsel(msel)
      .pipe(
        tap(
          (s) => {
            this.mselStore.setLoading(false);
            this.setAsDates(s);
            this.mselStore.add(s);
          },
          (error) => {
            this.mselStore.setLoading(false);
          }
        ),
        take(1)
      );
  }

  join(mselId: string, teamId: string) {
    return this.mselService.joinMselByInvitation(mselId, teamId);
  }

  launch(mselId: string, teamId: string): Observable<Msel> {
    return this.mselService.launchMselByInvitation(mselId, teamId);
  }

  copy(id: string) {
    this.mselStore.setLoading(true);
    this.mselService
      .copyMsel(id)
      .pipe(
        tap(() => {
          this.mselStore.setLoading(false);
        }),
        take(1)
      )
      .subscribe(
        (s) => {
          this.setAsDates(s);
          this.mselStore.add(s);
        },
        (error) => {
          this.mselStore.setLoading(false);
        }
      );
  }

  updateMsel(msel: Msel): Observable<Msel> {
    this.mselStore.setLoading(true);
    const update$ = this.mselService.updateMsel(msel.id, msel).pipe(
      take(1),
      tap({
        next: (n) => {
          this.mselStore.setLoading(false);
          this.setAsDates(n);
          this.updateStore(n);
        },
        error: () => {
          // A failed save used to be swallowed here, so the user saw no error and the form
          // reset itself as though the edit had been persisted.
          this.mselStore.setLoading(false);
        },
      }),
      shareReplay(1)
    );
    // Subscribed here as well as returned, so the store stays updated for the several callers
    // that fire and forget. shareReplay keeps a caller that *does* subscribe from issuing a
    // second PUT, and replays the outcome to it even though it subscribes after this line.
    update$.subscribe({
      next: () => {},
      error: (error) => {
        this.errorService.handleError(error);
      },
    });
    return update$;
  }

  pushIntegrations(mselId: string) {
    this.mselStore.setLoading(true);
    this.mselService
      .pushIntegrations(mselId)
      .pipe(
        tap(() => {
          this.mselStore.setLoading(false);
        }),
        take(1)
      )
      .subscribe(
        (n) => {
          this.updateStore(n);
        },
        (error) => {
          this.mselStore.setLoading(false);
          this.errorService.handleError(error);
        }
      );
  }

  pullIntegrations(mselId: string) {
    this.mselStore.setLoading(true);
    this.mselService
      .pullIntegrations(mselId)
      .pipe(
        tap(() => {
          this.mselStore.setLoading(false);
        }),
        take(1)
      )
      .subscribe(
        (n) => {
          this.updateStore(n);
        },
        (error) => {
          this.mselStore.setLoading(false);
          this.errorService.handleError(error);
        }
      );
  }

  cancelIntegrations(mselId: string) {
    this.mselService
      .cancelIntegrations(mselId)
      .pipe(take(1))
      .subscribe(
        () => {},
        (error) => {
          this.errorService.handleError(error);
        }
      );
  }

  archive(mselId: string) {
    this.mselStore.setLoading(true);
    this.mselService
      .archive(mselId)
      .pipe(
        tap(() => {
          this.mselStore.setLoading(false);
        }),
        take(1)
      )
      .subscribe(
        (n) => {
          this.updateStore(n);
        },
        (error) => {
          this.mselStore.setLoading(false);
        }
      );
  }

  delete(id: string) {
    this.mselService
      .deleteMsel(id)
      .pipe(take(1))
      .subscribe((r) => {
        this.deleteFromStore(id);
      });
  }

  addUserMselRole(userId: string, mselId: string, mselRole: MselRole) {
    this.mselStore.setLoading(true);
    this.mselService
      .addUserMselRole(userId, mselId, mselRole)
      .pipe(
        tap(() => {
          this.mselStore.setLoading(false);
        }),
        take(1)
      )
      .subscribe(
        (n) => {
          this.updateStore(n);
        },
        (error) => {
          this.mselStore.setLoading(false);
        }
      );
  }

  removeUserMselRole(userId: string, mselId: string, mselRole: MselRole) {
    this.mselStore.setLoading(true);
    this.mselService
      .removeUserMselRole(userId, mselId, mselRole)
      .pipe(
        tap(() => {
          this.mselStore.setLoading(false);
        }),
        take(1)
      )
      .subscribe(
        (n) => {
          this.updateStore(n);
        },
        (error) => {
          this.mselStore.setLoading(false);
        }
      );
  }

  downloadXlsx(id: string) {
    return this.mselService.downloadXlsx(id);
  }

  downloadJson(id: string) {
    return this.mselService.downloadJsonMsel(id);
  }

  uploadXlsx(
    mselId: string,
    file: File,
    observe: any,
    reportProgress: boolean
  ) {
    this.mselStore.setLoading(true);
    if (mselId) {
      this.mselService
        // the third argument is the form's optional MselId, which only guards
        // against a mismatch with the id in the route; the route id is enough
        .replaceWithXlsxFile(mselId, file, undefined, observe, reportProgress)
        .subscribe(
          (event: any) => {
            if (event.type === HttpEventType.UploadProgress) {
              const uploadProgress = Math.round(
                (100 * event.loaded) / event.total
              );
              this.uploadProgress.next(uploadProgress);
            } else if (event instanceof HttpResponse) {
              this.uploadProgress.next(0);
              this.mselStore.setLoading(false);
              if (event.status === 200) {
                const msel = event.body;
                this.mselStore.upsert(msel.id, msel);
              }
            }
          },
          (error) => {
            this.mselStore.setLoading(false);
            this.uploadProgress.next(0);
            this.errorService.handleError(error);
          }
        );
    } else {
      this.mselService
        .uploadXlsx(file, observe, reportProgress)
        .subscribe(
          (event) => {
            if (event.type === HttpEventType.UploadProgress) {
              const uploadProgress = Math.round(
                (100 * event.loaded) / event.total
              );
              this.uploadProgress.next(uploadProgress);
            } else if (event instanceof HttpResponse) {
              this.uploadProgress.next(0);
              this.mselStore.setLoading(false);
              if (event.status === 200) {
                const msel = event.body;
                this.mselStore.upsert(msel.id, msel);
              }
            }
          },
          (error) => {
            this.mselStore.setLoading(false);
            this.uploadProgress.next(0);
            this.errorService.handleError(error);
          }
        );
    }
  }

  uploadJson(file: File, observe: any, reportProgress: boolean) {
    this.mselStore.setLoading(true);
    this.mselService
      .uploadJsonMsel(file, observe, reportProgress)
      .subscribe(
        (event: any) => {
          if (event.type === HttpEventType.UploadProgress) {
            const uploadProgress = Math.round(
              (100 * event.loaded) / event.total
            );
            this.uploadProgress.next(uploadProgress);
          } else if (event instanceof HttpResponse) {
            this.uploadProgress.next(0);
            this.mselStore.setLoading(false);
            if (event.status === 200) {
              const msel = event.body;
              this.mselStore.upsert(msel.id, msel);
            }
          }
        },
        (error) => {
          this.mselStore.setLoading(false);
          this.uploadProgress.next(0);
          this.errorService.handleError(error);
        }
      );
  }

  // Errors are left to the caller, which shows them in the import dialog
  importJson(file: File): Observable<MselImportResult> {
    return this.mselService
      .importJsonMsel(file)
      .pipe(
        tap((result) => this.mselStore.upsert(result.msel.id, result.msel))
      );
  }

  setPageEvent(pageEvent: PageEvent) {
    this.mselStore.update({ pageEvent: pageEvent });
  }

  updateStore(msel: Msel) {
    this.mselStore.upsert(msel.id, msel);
  }

  updateIntegrationStatus(mselId: string, integrationStatus: string) {
    this.mselStore.update(mselId, { integrationStatus });
  }

  deleteFromStore(id: string) {
    this.mselStore.remove(id);
  }

  setActive(id: string) {
    this.mselStore.setActive(id);
  }

  private sortMsels(a: Msel, b: Msel, column: string, isAsc: boolean) {
    switch (column) {
      case 'description':
        return (
          (a.description.toLowerCase() < b.description.toLowerCase() ? -1 : 1) *
          (isAsc ? 1 : -1)
        );
      case 'dateCreated':
        return (
          (a.dateCreated.valueOf() < b.dateCreated.valueOf() ? -1 : 1) *
          (isAsc ? 1 : -1)
        );
      default:
        return 0;
    }
  }

  setAsDates(msel: Msel) {
    // set to a date object.
    msel.dateCreated = new Date(msel.dateCreated);
    msel.dateModified = new Date(msel.dateModified);
    msel.startTime = new Date(msel.startTime);
  }
}
